import { Head } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { StatusIndicator } from "@/components/status-indicator"
import { Pagination } from "@/components/pagination"
import { EmptyState } from "@/components/empty-state"
import { OrganisationFormDialog } from "@/components/organisation-form-dialog"
import { useDebouncedSearch } from "@/hooks/use-debounced-search"
import { Plus, Search, Pencil, Building2 } from "lucide-react"
import { useState } from "react"
import type { Organisation, PagyMetadata } from "@/types"

type Department = { code: string; name: string }
type CommuneName = { code: string; name: string; department_code: string }

type PartnerOrg = { id: string; name: string }

type Props = {
  organisations: Organisation[]
  filters: Record<string, string>
  pagy: PagyMetadata
  departments: Department[]
  commune_names: CommuneName[]
  all_organisations: PartnerOrg[]
}

const typeLabels: Record<string, string> = {
  promotor: "Promoteur",
  reseller: "Revendeur",
  agency: "Agence",
}

function OrganisationsIndex({ organisations, filters, pagy, departments, commune_names, all_organisations }: Props) {
  const [search, setSearch, searchRef] = useDebouncedSearch(filters.name_cont || "", "/organisations", "name_cont")

  const [showForm, setShowForm] = useState(false)
  const [editingOrg, setEditingOrg] = useState<Organisation | null>(null)

  return (
    <>
      <Head title="Organisations" />
      <PageHeader title="Organisations" />
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              ref={searchRef}
              placeholder="Rechercher par nom..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary">
              {pagy.count} résultat{pagy.count > 1 ? "s" : ""}
            </Badge>
            <Button
              onClick={() => {
                setEditingOrg(null)
                setShowForm(true)
              }}
            >
              <Plus className="size-4" />
              Nouvelle organisation
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="px-0">
            {organisations.length === 0 ? (
              <EmptyState
                icon={Building2}
                title="Aucune organisation trouvée"
                description="Essayez de modifier vos critères de recherche."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Démo</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organisations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell className="font-medium">{org.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{typeLabels[org.organisation_type]}</Badge>
                      </TableCell>
                      <TableCell>
                        <StatusIndicator active={org.is_active} />
                      </TableCell>
                      <TableCell>{org.is_demo ? "Oui" : "Non"}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingOrg(org)
                            setShowForm(true)
                          }}
                          title="Modifier"
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
          <CardFooter>
            <Pagination pagy={pagy} baseUrl="/organisations" filters={filters} className="w-full p-0" />
          </CardFooter>
        </Card>
      </div>

      <OrganisationFormDialog open={showForm} onOpenChange={setShowForm} organisation={editingOrg} departments={departments} communeNames={commune_names} allOrganisations={all_organisations} />
    </>
  )
}

OrganisationsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default OrganisationsIndex
